import React, { useMemo } from 'react';
import type { UfsaMitgeltendeUnterlage } from '../../types';
import {
    mitgeltendeUnterlagenListe,
    uebernehmePlanungUnterlage,
    unterlagenAusPlanung,
    type PlanungUnterlageKurz,
} from './ufsaMitgeltendeUnterlagen';
import { ClipboardList, FileCheck2, Plus } from 'lucide-react';

interface Props {
    /** Relevante Zeilen der Unterlagen-Checkliste (Steckbrief); `null`, solange nicht gelesen. */
    unterlagen: readonly PlanungUnterlageKurz[] | null;
    gespeichert: UfsaMitgeltendeUnterlage[] | undefined;
    /** Neue Liste — läuft über projectData und damit über das Autospeichern der Ansicht. */
    onChange: (liste: UfsaMitgeltendeUnterlage[]) => void;
    isReadOnly?: boolean;
}

/**
 * Vorschläge „Aus der Planungsphase" für den Abschnitt „Mitgeltende
 * Unterlagen": Unterlagen, die dort als vorliegend geführt werden und für
 * spätere Arbeiten gelten. Übernahme nur per Klick — die Liste der
 * Unterlage wird nie beim Laden befüllt.
 */
export const UfsaPlanungUnterlagenVorschlaege: React.FC<Props> = ({ unterlagen, gespeichert, onChange, isReadOnly = false }) => {
    const vorschlaege = useMemo(() => unterlagenAusPlanung(unterlagen, gespeichert), [unterlagen, gespeichert]);
    if (isReadOnly || !unterlagen || vorschlaege.length === 0) return null;

    const uebernehmen = (v: (typeof vorschlaege)[number]) => {
        const liste = mitgeltendeUnterlagenListe(gespeichert);
        const next = uebernehmePlanungUnterlage(liste, v, crypto.randomUUID());
        if (next !== liste) onChange(next);
    };

    return (
        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
                <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700 shrink-0">
                    <ClipboardList className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        Aus der Planungsphase
                        <span className="text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-200 font-medium">{vorschlaege.length}</span>
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Unterlagen, die die Planungsphase als vorliegend führt und die für spätere Arbeiten gelten. Übernehmen Sie, was der Betreiber tatsächlich erhält; den Ablageort tragen Sie in der Liste nach.
                    </p>
                </div>
            </div>
            <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-2">
                {vorschlaege.map(v => (
                    <div key={v.id} className="border border-slate-200 rounded-lg bg-white flex items-start justify-between gap-3 p-3">
                        <div className="min-w-0">
                            <div className="text-sm font-bold text-slate-800 break-words">
                                {v.name}
                                {v.dokument?.kurz && <span className="ml-1.5 text-xs font-medium text-slate-400">({v.dokument.kurz})</span>}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                                <span>liegt vor</span>
                                {v.beleg && (
                                    <span className="inline-flex items-center gap-1 text-emerald-700">
                                        <FileCheck2 className="w-3.5 h-3.5" /> Beleg in der Projektablage
                                    </span>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => uebernehmen(v)}
                            title={`„${v.name}“ in die Liste übernehmen`}
                            className="shrink-0 px-2.5 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md inline-flex items-center gap-1.5 transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" /> Übernehmen
                        </button>
                    </div>
                ))}
            </div>
        </section>
    );
};
